// material-ui
import { useTheme } from "@emotion/react";
import HomeIcon from "@mui/icons-material/Home";
import {
  Alert,
  Box,
  Card,
  Grid,
  Breadcrumbs as MuiBreadcrumbs,
  Stack,
  Typography,
} from "@mui/material";
import Link from "@mui/material/Link";
import { IconChevronRight } from "@tabler/icons-react";
import { useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useParams } from "react-router-dom";
import { getAlbum } from "store/slices/albums";
import Loader from "ui-component/Loader";
import Error from "views/ui-elements/Error";
import AlbumAccessibility from "./AlbumAccessibility";
import { resolveImageURI } from "./album-utils";

const AlbumLayout = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { album, isLoadingAlbum, scriptStatus } = useSelector(
    (state) => state.albums
  );
  const subMenuOpen = useSelector((state) => state.menu.openItem);
  const { id } = useParams();

  const refresh = useCallback(() => {
    dispatch(getAlbum(id, true));
  }, [dispatch, id]);

  useEffect(() => {
    refresh();
    const intervalId = setInterval(refresh, 5 * 1000);
    return () => clearInterval(intervalId);
  }, [refresh]);

  const linkSX = {
    display: "flex",
    color: "grey.900",
    textDecoration: "none",
    alignContent: "center",
    alignItems: "center",
  };

  const iconStyle = {
    marginRight: theme.spacing(0.75),
    marginTop: `-${theme.spacing(0.25)}`,
    width: "1rem",
    height: "1rem",
    color: theme.palette.secondary.main,
  };

  if (isLoadingAlbum) {
    return (
      <>
        <Loader />
      </>
    );
  }
  if (!album) {
    return <Error />;
  }

  const isAnalysisPending = album ? album.has_pending_analysis : false;
  const isFaceRekoPending = album.is_correct_identifications_running
    ? album.is_correct_identifications_running
    : false;
  const isMainPhotoKeyMissing = album.main_photo_key ? false : true;
  return (
    <>
      <Card
        sx={{
          marginBottom: 1,
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.primary[200] + 75,
          background: theme.palette.background.default,
        }}
      >
        <Box sx={{ p: 2, pl: 2 }}>
          <Grid
            container
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            spacing={1}
          >
            <Grid item>
              <Stack direction="row" alignItems="center" spacing={1}>
                {album && album.main_photo_key && (
                  <Box
                    component={"img"}
                    height={30}
                    src={resolveImageURI(album.main_photo_key)}
                  />
                )}
                <Typography variant="h3" sx={{ fontWeight: 500 }}>
                  {album && album.name}
                </Typography>
                <AlbumAccessibility album={album} />
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {album && album.description}
              </Typography>
            </Grid>
            <Grid textAlign="right">
              <MuiBreadcrumbs
                sx={{
                  "& .MuiBreadcrumbs-separator": {
                    width: 16,
                    ml: 1.25,
                    mr: 1.25,
                    textAlign: "center",
                  },
                }}
                maxItems={5}
                separator={<IconChevronRight stroke={1.5} size="1rem" />}
              >
                <Typography
                  component={Link}
                  to="/"
                  color="inherit"
                  variant="subtitle1"
                  sx={linkSX}
                >
                  <HomeIcon sx={iconStyle} />
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    display: "flex",
                    textDecoration: "none",
                    alignContent: "center",
                    alignItems: "center",
                    color: "grey.500",
                  }}
                >
                  <FormattedMessage id="menu.albums" />
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    display: "flex",
                    textDecoration: "none",
                    alignContent: "center",
                    alignItems: "center",
                    color: "grey.500",
                  }}
                >
                  <FormattedMessage
                    id="album.number"
                    values={{ id: album && album.id }}
                  />
                </Typography>
                {subMenuOpen && subMenuOpen.length === 1 && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      display: "flex",
                      textDecoration: "none",
                      alignContent: "center",
                      alignItems: "center",
                      color: "grey.500",
                    }}
                  >
                    <FormattedMessage id={`menu.${subMenuOpen[0]}`} />
                  </Typography>
                )}
              </MuiBreadcrumbs>
            </Grid>
            {isMainPhotoKeyMissing && (
              <Grid item xs={12}>
                <Alert sx={{ height: 30 }} severity="warning" variant="filled">
                  <FormattedMessage id="album.is_main_photo_key_missing" />
                </Alert>
              </Grid>
            )}
            {isAnalysisPending && (
              <Grid item xs={12}>
                <Alert
                  sx={{ height: 30 }}
                  severity="warning"
                  variant="standard"
                >
                  <FormattedMessage
                    id="album.isAnalysisPending"
                    values={{
                      analyzed_count: album.analyzed_count,
                      total_count: album.photos_count - album.errored_count,
                    }}
                  />
                </Alert>
              </Grid>
            )}
            {isFaceRekoPending && (
              <Grid item xs={12}>
                <Alert sx={{ height: 30 }} severity="info" variant="standard">
                  <FormattedMessage id="album.is_script_running" />
                </Alert>
              </Grid>
            )}
            {scriptStatus.isAvailable && (
              <Grid item xs={12}>
                <Alert sx={{ height: 30 }} severity="info" variant="standard">
                  <FormattedMessage
                    id="script.available"
                    values={{
                      nb_identifications:
                        scriptStatus.nb_identifications_to_process,
                      nb_photos: scriptStatus.nb_photos_to_process,
                    }}
                  />
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </Card>
      <Outlet />
    </>
  );
};

export default AlbumLayout;
